import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { BookingRequest, BookingSelection, Customer, DEFAULT_DONATION, InvoiceResponse, emptySelection } from 'types';

/** Everything the user has entered so far. Kept in sessionStorage so a refresh or the return from Stripe doesn't lose it. */
export interface Draft {
  selection: BookingSelection; // donation is only meaningful via `donate` / `donationText`, see toRequest
  customer: Customer;
  donate: boolean;
  donationText: string;
  invoice?: InvoiceResponse & { fingerprint: string }; // the Stripe invoice created for exactly this booking
}

const KEY = 'pujo26-booking';
const blank = (): Draft => ({ selection: emptySelection(), customer: { name: '', email: '' }, donate: false, donationText: String(DEFAULT_DONATION) });

const load = (): Draft => {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (raw) return { ...blank(), ...JSON.parse(raw) };
  } catch {}
  return blank();
};

/** Donation entered by the user: 0 when not donating, NaN when the amount is not a valid number. */
export const donationOf = (d: Draft) => (d.donate ? (/^\d+([.,]\d{1,2})?$/.test(d.donationText.trim()) ? Number(d.donationText.trim().replace(',', '.')) : NaN) : 0);

export const toRequest = (d: Draft): BookingRequest => ({ ...d.selection, donation: donationOf(d) || 0, customer: { name: d.customer.name.trim(), email: d.customer.email.trim() } });

interface Ctx {
  draft: Draft;
  update: (patch: Partial<Draft>) => void;
  reset: () => void;
}
const BookingContext = createContext<Ctx>(null as unknown as Ctx);
export const useBooking = () => useContext(BookingContext);

export function BookingProvider({ children }: { children: React.ReactNode }) {
  const [draft, setDraft] = useState<Draft>(load);
  useEffect(() => {
    try {
      sessionStorage.setItem(KEY, JSON.stringify(draft));
    } catch {}
  }, [draft]);
  const update = useCallback((patch: Partial<Draft>) => setDraft((d) => ({ ...d, ...patch })), []);
  const reset = useCallback(() => setDraft(blank()), []);
  return <BookingContext.Provider value={{ draft, update, reset }}>{children}</BookingContext.Provider>;
}
